import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { Api } from '../../../api/api';
import { getLocationSettings, updateLocationGeo, updateLocationSettings } from '../../../api/functions';
import { LocationGeo } from '../../../api/models/location-geo';
import { LocationSettingField } from '../../../api/models/location-setting-field';
import { LocationSettingsResponse } from '../../../api/models/location-settings-response';
import { LocationSettingsUpdateRequest } from '../../../api/models/location-settings-update-request';
import { LocationStateService } from '../../../common/services/location-state.service';
import { formatApiError } from '../../../common/utils/api-error';
import { LocationTariffsComponent } from './location-tariffs.component';

type Phase = 'idle' | 'loading' | 'saving' | 'saved' | 'error';
type Grouping = 'category' | 'suggested';

/** Kategorie der Schlüssel, die kein Code liest. */
const UNKNOWN = 'unknown';

/**
 * Reihenfolge und Beschriftung der Kategorien — die verschachtelten Objekte der Metadaten — und der
 * Sammelgruppe für Schlüssel, die kein Code liest.
 */
const CATEGORIES: readonly { id: string; label: string; badge: string }[] = [
  { id: UNKNOWN, label: 'Unbekannt', badge: 'bg-red-100 text-red-900' },
  { id: 'optimizer', label: 'Optimierer', badge: 'bg-emerald-100 text-emerald-900' },
  { id: 'forecast', label: 'Prognose', badge: 'bg-violet-100 text-violet-900' },
  { id: 'displayChart', label: 'Chart-Anzeige', badge: 'bg-sky-100 text-sky-900' },
  { id: 'schedule', label: 'Fahrplan', badge: 'bg-orange-100 text-orange-900' },
];

/** Beschriftung der vorgeschlagenen Unterthemen (`<kategorie>/<thema>`). */
const TOPICS: Record<string, string> = {
  battery: 'Batterie',
  grid: 'Netz',
  peakPower: 'Leistungspreis',
  emergencyPower: 'Notstrom',
  window: 'Zeitfenster',
  liveChart: 'Live-Chart',
  day: 'Tagescharts',
  origin: 'Herkunftscharts',
  modes: 'Betriebsmodi',
  gridSetpoints: 'Netz-Sollwerte',
  thresholds: 'Schwellen und Rundung',
  socTargets: 'SOC-Ziele',
  horizon: 'Horizont',
  reserves: 'Reserven',
  fullBattery: 'Volle Batterie',
  publishing: 'Veröffentlichung',
};

/**
 * Eine Tabellenzeile: ein bekannter Schlüssel des Katalogs, einer, der noch auf oberster Ebene liegt
 * (`movesTo` nennt sein Ziel; er steht in dessen Kategorie direkt darüber und ist nicht bearbeitbar), oder
 * einer, den kein Code liest.
 */
interface Row {
  key: string;
  field: LocationSettingField | null;
  movesTo: string | null;
  category: string;
  suggestedCategory: string;
  original: unknown;
  /** Der gespeicherte Wert als Text, so wie er im Eingabefeld steht. */
  originalText: string;
}

interface Group {
  id: string;
  label: string;
  rows: Row[];
}

type GeoKey = 'latitude' | 'longitude' | 'timezone';

/** Die Felder der Geo-Position mit Beschriftung und erlaubtem Bereich (nur Koordinaten). */
const GEO_FIELDS: readonly { key: GeoKey; label: string; unit: string; limit: number | null; hint: string }[] = [
  { key: 'latitude', label: 'Breitengrad', unit: '°', limit: 90, hint: '-90 bis 90' },
  { key: 'longitude', label: 'Längengrad', unit: '°', limit: 180, hint: '-180 bis 180' },
  { key: 'timezone', label: 'Zeitzone', unit: '', limit: null, hint: 'IANA, z. B. Europe/Vienna' },
];

interface ParsedValue {
  value: unknown;
  error: string | null;
}

/**
 * Bearbeitung der `metadata` eines Standorts als Tabelle. Jede Zeile ist ein Schlüssel, den
 * `LocationSettings` liest, oder einer, den kein Code liest — die sind rot markiert und lassen sich hier
 * einzeln oder mit `migration_scripts/location_metadata_remove_unused_keys.sql` für alle Standorte entfernen.
 * Schlüssel, die noch auf oberster Ebene liegen und erst `location_metadata_move_to_categories.sql` in ihre
 * Kategorie verschiebt, liest das Backend als Rückfall weiter; sie stehen gelb über ihrem Ziel und sind gesperrt.
 */
@Component({
  selector: 'app-location-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MatFormFieldModule, MatOptionModule, MatSelectModule, LocationTariffsComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './location-settings.component.html',
})
export class LocationSettingsComponent {
  private readonly api = inject(Api);
  protected readonly locationState = inject(LocationStateService);

  readonly settingsPath = getLocationSettings.PATH;
  readonly geoPath = updateLocationGeo.PATH;
  readonly geoFields = GEO_FIELDS;
  readonly categories = CATEGORIES;

  readonly selectedLocationId = computed(() => this.locationState.currentLocation()?.id ?? '');

  readonly settings = signal<LocationSettingsResponse | null>(null);
  readonly drafts = signal<Record<string, string>>({});
  readonly phase = signal<Phase>('idle');
  readonly message = signal<string | null>(null);

  /** Geo-Position (Spalte `geo`, nicht `metadata`); eigener Entwurf und eigenes Speichern. */
  readonly geoDrafts = signal<Partial<Record<GeoKey, string>>>({});
  readonly geoPhase = signal<Phase>('idle');
  readonly geoMessage = signal<string | null>(null);

  readonly geoOriginal = computed<Record<GeoKey, string>>(() => {
    const geo = this.settings()?.geo;
    return {
      latitude: geo?.latitude != null ? String(geo.latitude) : '',
      longitude: geo?.longitude != null ? String(geo.longitude) : '',
      timezone: geo?.timezone ?? '',
    };
  });

  readonly geoErrors = computed<Partial<Record<GeoKey, string>>>(() => {
    const errors: Partial<Record<GeoKey, string>> = {};
    for (const field of GEO_FIELDS) {
      const text = this.geoDraftOf(field.key).trim();
      if (field.limit === null || text === '') {
        continue;
      }
      const number = Number(text.replace(',', '.'));
      if (!Number.isFinite(number)) {
        errors[field.key] = 'Keine Zahl';
      } else if (Math.abs(number) > field.limit) {
        errors[field.key] = `Erlaubt: ${field.hint}`;
      }
    }
    return errors;
  });

  readonly geoChanged = computed(() => GEO_FIELDS.some((field) => this.isGeoChanged(field.key)));
  readonly geoCanSave = computed(
    () =>
      this.geoChanged() &&
      Object.keys(this.geoErrors()).length === 0 &&
      this.geoPhase() !== 'saving' &&
      !this.isBusy(),
  );

  readonly search = signal('');
  readonly grouping = signal<Grouping>('category');
  readonly hiddenCategories = signal<ReadonlySet<string>>(new Set());

  readonly rows = computed<Row[]>(() => {
    const settings = this.settings();
    if (!settings) {
      return [];
    }
    const pending = new Map<string, Row>();
    const unknown: Row[] = [];
    for (const key of settings.unknownKeys) {
      const original = valueAt(settings.metadata, key);
      const target = key.includes('.') ? undefined : settings.fields.find((field) => field.key.endsWith(`.${key}`));
      const row: Row = {
        key,
        field: null,
        movesTo: target?.key ?? null,
        category: target?.category ?? UNKNOWN,
        suggestedCategory: target ? target.suggestedCategory : `${UNKNOWN}/remove`,
        original,
        originalText: toText(original, null),
      };
      target ? pending.set(target.key, row) : unknown.push(row);
    }
    const known = settings.fields.flatMap((field) => {
      const original = valueAt(settings.metadata, field.key);
      const row: Row = {
        key: field.key,
        field,
        movesTo: null,
        category: field.category,
        suggestedCategory: field.suggestedCategory,
        original,
        originalText: toText(original, field),
      };
      const leftover = pending.get(field.key);
      return leftover ? [leftover, row] : [row];
    });
    return [...unknown, ...known];
  });

  readonly parsed = computed<Record<string, ParsedValue>>(() => {
    const drafts = this.drafts();
    const result: Record<string, ParsedValue> = {};
    for (const row of this.rows()) {
      const text = drafts[row.key] ?? row.originalText;
      result[row.key] = text === row.originalText ? { value: row.original, error: null } : parse(text, row.field);
    }
    return result;
  });

  readonly changedKeys = computed(() =>
    this.rows()
      .filter((row) => this.isChanged(row))
      .map((row) => row.key),
  );

  readonly invalidKeys = computed(() =>
    Object.entries(this.parsed())
      .filter(([, parsed]) => parsed.error)
      .map(([key]) => key),
  );

  /** Schlüssel, die kein Code liest; ohne die noch nicht verschobenen. */
  readonly unknownCount = computed(() => this.rows().filter((row) => row.category === UNKNOWN).length);

  /** Spalten der Tabelle; das Thema fällt in der Gruppierung nach Vorschlag weg, es steht in der Überschrift. */
  readonly columnCount = computed(() => (this.grouping() === 'category' ? 6 : 5));

  readonly isBusy = computed(() => this.phase() === 'loading' || this.phase() === 'saving');
  readonly canSave = computed(
    () => !this.isBusy() && this.changedKeys().length > 0 && this.invalidKeys().length === 0,
  );

  readonly categoryCounts = computed(() => {
    const counts: Record<string, { total: number; set: number }> = {};
    for (const row of this.rows().filter((row) => !row.movesTo)) {
      const count = (counts[row.category] ??= { total: 0, set: 0 });
      count.total++;
      if (row.original !== undefined) {
        count.set++;
      }
    }
    return counts;
  });

  readonly groups = computed<Group[]>(() => {
    const term = this.search().trim().toLowerCase();
    const hidden = this.hiddenCategories();
    const grouping = this.grouping();
    const visible = this.rows().filter((row) => {
      if (hidden.has(row.category)) {
        return false;
      }
      return (
        !term ||
        row.key.toLowerCase().includes(term) ||
        (row.field?.description ?? '').toLowerCase().includes(term) ||
        this.suggestedLabel(row.suggestedCategory).toLowerCase().includes(term)
      );
    });

    const groups = new Map<string, Group>();
    for (const row of visible) {
      const id = grouping === 'category' ? row.category : row.suggestedCategory;
      const label = grouping === 'category' ? this.categoryLabel(id) : this.suggestedLabel(id);
      let group = groups.get(id);
      if (!group) {
        group = { id, label, rows: [] };
        groups.set(id, group);
      }
      group.rows.push(row);
    }
    return [...groups.values()].sort((a, b) => categoryOrder(a.id) - categoryOrder(b.id));
  });

  constructor() {
    effect(() => {
      const locationId = this.selectedLocationId();
      if (locationId) {
        untracked(() => this.load(locationId));
      }
    });
  }

  setLocationId(locationId: string): void {
    if ((this.changedKeys().length > 0 || this.geoChanged()) && !confirm('Ungespeicherte Änderungen verwerfen?')) {
      return;
    }
    const next = this.locationState.locations().find((location) => location.id === locationId);
    if (next) {
      this.locationState.setLocation(next);
    }
  }

  setDraft(key: string, text: string): void {
    this.drafts.update((drafts) => ({ ...drafts, [key]: text }));
    if (this.phase() === 'saved') {
      this.phase.set('idle');
      this.message.set(null);
    }
  }

  /** Leert den Wert; beim Speichern verschwindet der Schlüssel aus den Metadaten. */
  clear(row: Row): void {
    this.setDraft(row.key, '');
  }

  revert(row: Row): void {
    this.drafts.update((drafts) => {
      const next = { ...drafts };
      delete next[row.key];
      return next;
    });
  }

  /** Entfernt alle Schlüssel, die kein Code liest, aus diesem Standort. */
  clearUnknown(): void {
    this.rows()
      .filter((row) => row.category === UNKNOWN)
      .forEach((row) => this.clear(row));
  }

  resetAll(): void {
    this.drafts.set({});
    this.message.set(null);
    this.phase.set('idle');
  }

  toggleCategory(id: string): void {
    this.hiddenCategories.update((hidden) => {
      const next = new Set(hidden);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async save(): Promise<void> {
    const settings = this.settings();
    if (!settings || !this.canSave()) {
      return;
    }
    this.phase.set('saving');
    this.message.set(null);
    try {
      const response = await this.api.invoke(updateLocationSettings, {
        location_id: settings.locationId,
        body: { metadata: this.buildMetadata() },
      });
      this.settings.set(response);
      this.drafts.set({});
      this.phase.set('saved');
      this.message.set(`Gespeichert: ${settings.locationName ?? settings.locationId}.`);
    } catch (error) {
      this.phase.set('error');
      this.message.set(formatApiError(error));
    }
  }

  geoDraftOf(key: GeoKey): string {
    return this.geoDrafts()[key] ?? this.geoOriginal()[key];
  }

  isGeoChanged(key: GeoKey): boolean {
    const draft = this.geoDrafts()[key];
    return draft !== undefined && draft.trim() !== this.geoOriginal()[key];
  }

  setGeoDraft(key: GeoKey, text: string): void {
    this.geoDrafts.update((drafts) => ({ ...drafts, [key]: text }));
    if (this.geoPhase() === 'saved') {
      this.geoPhase.set('idle');
      this.geoMessage.set(null);
    }
  }

  resetGeo(): void {
    this.geoDrafts.set({});
    this.geoPhase.set('idle');
    this.geoMessage.set(null);
  }

  /** Speichert nur die Geo-Position; ungespeicherte Änderungen an den Metadaten bleiben stehen. */
  async saveGeo(): Promise<void> {
    const settings = this.settings();
    if (!settings || !this.geoCanSave()) {
      return;
    }
    const number = (key: GeoKey) => {
      const text = this.geoDraftOf(key).trim();
      return text === '' ? undefined : Number(text.replace(',', '.'));
    };
    const body: LocationGeo = {
      latitude: number('latitude'),
      longitude: number('longitude'),
      timezone: this.geoDraftOf('timezone').trim() || undefined,
    };
    this.geoPhase.set('saving');
    this.geoMessage.set(null);
    try {
      const response = await this.api.invoke(updateLocationGeo, { location_id: settings.locationId, body });
      this.settings.set(response);
      this.geoDrafts.set({});
      this.geoPhase.set('saved');
      this.geoMessage.set('Geo-Position gespeichert.');
      // Der Rest der App liest Position und Zeitzone aus dem gewählten Standort.
      const current = this.locationState.currentLocation();
      if (current?.id === response.locationId) {
        this.locationState.setLocation({
          ...current,
          latitude: response.geo.latitude,
          longitude: response.geo.longitude,
          timezone: response.geo.timezone,
        });
      }
    } catch (error) {
      this.geoPhase.set('error');
      this.geoMessage.set(formatApiError(error));
    }
  }

  isChanged(row: Row): boolean {
    const draft = this.drafts()[row.key];
    return draft !== undefined && draft !== row.originalText;
  }

  draftOf(row: Row): string {
    return this.drafts()[row.key] ?? row.originalText;
  }

  errorOf(row: Row): string | null {
    return this.parsed()[row.key]?.error ?? null;
  }

  categoryLabel(id: string): string {
    return CATEGORIES.find((category) => category.id === id)?.label ?? id;
  }

  suggestedLabel(id: string): string {
    if (id === `${UNKNOWN}/remove`) {
      return 'Entfernen';
    }
    return `${this.categoryLabel(topLevel(id))} › ${this.topicLabel(id)}`;
  }

  /** Nur das Unterthema; die Kategorie steht schon in der Gruppenüberschrift. */
  topicLabel(id: string): string {
    if (id === `${UNKNOWN}/remove`) {
      return 'Entfernen';
    }
    const topic = id.split('/')[1] ?? '';
    return TOPICS[topic] ?? topic;
  }

  private async load(locationId: string): Promise<void> {
    this.phase.set('loading');
    this.message.set(null);
    this.drafts.set({});
    this.resetGeo();
    try {
      const response = await this.api.invoke(getLocationSettings, { location_id: locationId });
      // Der Standort kann gewechselt haben, während die Antwort unterwegs war.
      if (this.selectedLocationId() === locationId) {
        this.settings.set(response);
        this.phase.set('idle');
      }
    } catch (error) {
      this.settings.set(null);
      this.phase.set('error');
      this.message.set(formatApiError(error));
    }
  }

  /** Die gespeicherten Metadaten mit den Änderungen; leere Werte fallen weg. */
  private buildMetadata(): LocationSettingsUpdateRequest['metadata'] {
    const parsed = this.parsed();
    const metadata: Record<string, unknown> = {};
    for (const row of this.rows()) {
      const value = parsed[row.key].value;
      if (value === undefined) {
        continue;
      }
      const [head, ...rest] = row.key.split('.');
      if (rest.length === 0) {
        metadata[head] = value;
      } else {
        const nested = (metadata[head] ??= {}) as Record<string, unknown>;
        nested[rest.join('.')] = value;
      }
    }
    return metadata as LocationSettingsUpdateRequest['metadata'];
  }
}

function valueAt(metadata: Record<string, unknown>, key: string): unknown {
  const [head, ...rest] = key.split('.');
  if (rest.length === 0) {
    return metadata[head] ?? undefined;
  }
  const nested = metadata[head];
  return nested && typeof nested === 'object' ? (nested as Record<string, unknown>)[rest.join('.')] ?? undefined : undefined;
}

function toText(value: unknown, field: LocationSettingField | null): string {
  if (value === undefined || value === null) {
    return '';
  }
  if (!field) {
    return JSON.stringify(value);
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

/** Text aus dem Eingabefeld als Wert; `undefined` heißt: Schlüssel entfernen. */
function parse(text: string, field: LocationSettingField | null): ParsedValue {
  const trimmed = text.trim();
  if (trimmed === '') {
    return { value: undefined, error: null };
  }
  if (!field) {
    try {
      return { value: JSON.parse(trimmed), error: null };
    } catch {
      return { value: undefined, error: 'Kein gültiges JSON' };
    }
  }
  switch (field.type) {
    case 'NUMBER':
    case 'INTEGER': {
      const number = Number(trimmed.replace(',', '.'));
      if (!Number.isFinite(number)) {
        return { value: undefined, error: 'Keine Zahl' };
      }
      if (field.type === 'INTEGER' && !Number.isInteger(number)) {
        return { value: undefined, error: 'Keine ganze Zahl' };
      }
      return { value: number, error: null };
    }
    case 'BOOLEAN':
      return { value: trimmed === 'true', error: null };
    case 'ENUM':
      return field.allowedValues?.includes(trimmed)
        ? { value: trimmed, error: null }
        : { value: undefined, error: 'Unbekannter Wert' };
  }
}

function topLevel(suggestedCategory: string): string {
  return suggestedCategory.split('/')[0];
}

/** Gruppen in der Reihenfolge von {@link CATEGORIES}; Unterthemen bleiben in der Reihenfolge des Katalogs. */
function categoryOrder(id: string): number {
  const index = CATEGORIES.findIndex((category) => category.id === topLevel(id));
  return index === -1 ? CATEGORIES.length : index;
}
